import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Nav } from '../../Nav';
import { Footer } from '../../Footer';
import { ArrowRight, Calendar } from 'lucide-react';
import { getApiBaseUrl } from '../../../config/api';

// Get API base URL from centralized config
const API_BASE_URL = getApiBaseUrl();

// Helper function to create slug from title
const createSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
};

const BlogCard = ({ blog, onClick }) => {
  return (
    <motion.div
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 h-full flex flex-col"
    >
      <div className="cursor-pointer" onClick={onClick}>
        <div className="h-48 overflow-hidden">
          <img
            src={
              blog.image_url ||
              "https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=500&q=80"
            }
            alt={blog.title}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            onError={(e) => {
              e.target.src =
                "https://images.unsplash.com/photo-1499750310107-5fef28a66643?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=500&q=80";
            }}
          />
        </div>
        <div className="p-6 flex-grow flex flex-col justify-between">
          <div>
            <div className="flex items-center text-sm text-gray-500 mb-2">
              <Calendar className="h-4 w-4 mr-1.5 text-violet-500" />
              <span>
                {new Date(blog.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
              {blog.title}
            </h3>
          </div>
          <div className="flex items-center text-violet-600 font-medium mt-4">
            Read more <ArrowRight className="h-4 w-4 ml-1" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const navigate = useNavigate();

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/blogs`);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Ensure blogs is always an array
        const blogsData = Array.isArray(data.data?.blogs) ? data.data.blogs : [];
        setBlogs(blogsData);
        setTotalBlogs(data.data?.total_blogs || 0);
      } else {
        throw new Error("Failed to fetch blogs");
      }
    } catch (err) {
      setError(err.message);
      console.error("Failed to fetch blogs:", err.message);
      // Fallback to mock data
      setBlogs([
        {
          blog_id: "mock-1",
          title: "Sample Blog Post 1 (Error Fallback)",
          date: new Date().toISOString(),
          image_url: "https://source.unsplash.com/random/800x500?technology,1",
        },
        {
          blog_id: "mock-2",
          title: "Sample Blog Post 2 (Error Fallback)",
          date: new Date(Date.now() - 86400000).toISOString(),
          image_url: "https://source.unsplash.com/random/800x500?technology,2",
        },
      ]);
      setTotalBlogs(2);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(page);
  }, [page]);

  const handleBlogClick = (blog) => {
    const slug = createSlug(blog.title);
    // Navigate with slug in URL and pass blogId in state for fetching
    navigate(`/blogs/${slug}`, { state: { blogId: blog.blog_id } });
  };

  const totalPages = Math.ceil(totalBlogs / 9);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center mt-12">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            Previous
          </button>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="w-full overflow-x-hidden min-h-screen flex flex-col">
        <Nav />
        <main className="flex-grow">
          <div className="relative pt-10 pb-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="relative z-10  text-center">
                <motion.h1
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { duration: 0.6 },
                    },
                  }}
                  className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-gray-900"
                >
                  Our Blog
                </motion.h1>
                <motion.p
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: { delay: 0.2, duration: 0.6 },
                    },
                  }}
                  className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto"
                >
                  Discover the latest insights, tutorials, and news about
                  AI-powered design and visual content creation.
                </motion.p>
              </div>
            </div>
          </div>

          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {loading && !blogs.length ? (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
                  <p className="ml-3 text-gray-600">Loading posts...</p>
                </div>
              ) : error ? (
                <div className="text-center py-10 px-4 bg-red-50 border border-red-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-red-700 mb-2">
                    Failed to Load Blog Posts
                  </h3>
                  <p className="text-red-600 mb-4">{error}</p>
                  <p className="text-gray-600">
                    Displaying sample content instead.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {Array.isArray(blogs) && blogs.map((blog) => (
                      <motion.div
                        key={blog.blog_id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true, amount: 0.1 }}
                      >
                        <BlogCard
                          blog={blog}
                          onClick={() => handleBlogClick(blog)}
                        />
                      </motion.div>
                    ))}
                  </div>
                  {renderPagination()}
                </>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};
